import { MouseEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { UseMiniMapControlsArgs } from "./types";
import { clientToSvgPoint } from "utils/math/clientToSvgPoint";
import { calculateZoomedViewBox } from "utils/math/calculateZoomedViewBox";
import { useKeyPressed } from "hooks/useKeyPressed";
import { useSvgPinchZoom } from "hooks/useSvgMapControls/useSvgPinchZoom";
import { useSvgViewBoxPan } from "hooks/useSvgMapControls/useSvgViewBoxPan";
import {
  DEFAULT_MINIMAP_CONFIG,
  MINIMAP_DISABLED_HANDLERS,
  MINIMAP_INTERACTION_MODES,
  MINIMAP_CURSORS,
} from "./constants";
import type { ViewBox } from "types/documentation";
import type { MiniMapConfig } from "./types";

export const useMiniMapControls = ({
  baseViewBox,
  isDisabled = false,
  interactionMode = MINIMAP_INTERACTION_MODES.VIEW,
  config: userConfig,
}: UseMiniMapControlsArgs) => {
  const config: MiniMapConfig = useMemo(
    () => ({
      ...DEFAULT_MINIMAP_CONFIG,
      ...userConfig,
      wheel: {
        ...DEFAULT_MINIMAP_CONFIG.wheel,
        ...userConfig?.wheel,
      },
    }),
    [userConfig],
  );

  const { minZoomWidth, wheel } = config;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const viewBoxRef = useRef<ViewBox>(baseViewBox);

  const updateViewBoxDom = useCallback((vb: ViewBox) => {
    const svg = svgRef.current;
    if (!svg) return;

    viewBoxRef.current = vb;
    svg.setAttribute("viewBox", `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
  }, []);

  const isEditActive = interactionMode === MINIMAP_INTERACTION_MODES.EDIT;

  const isSpacePressed = useKeyPressed({
    code: "Space",
    // Only listen for Space key if we are in edit mode
    isDisabled: isDisabled || !isEditActive,
    preventDefault: true,
  });

  useSvgPinchZoom({
    svgRef,
    viewBoxRef,
    baseViewBox,
    isDisabled,
    minZoomWidth,
    wheelConfig: wheel,
    onZoom: updateViewBoxDom,
  });

  // If in edit mode: Disable panning UNLESS Space is pressed.
  // If in view mode: Disable panning only if globally disabled.
  const isPanningDisabled = isEditActive ? !isSpacePressed : isDisabled;

  const panHandlers = useSvgViewBoxPan({
    svgRef,
    viewBoxRef,
    baseViewBox,
    isDisabled: isPanningDisabled,
    onPan: updateViewBoxDom,
  });

  const zoomIn = useCallback(() => {
    const vb = viewBoxRef.current;
    const cx = vb.x + vb.w / 2;
    const cy = vb.y + vb.h / 2;
    const newVb = calculateZoomedViewBox({
      zoomCenterX: cx,
      zoomCenterY: cy,
      factor: config.zoomInFactor,
      currentViewBox: vb,
      baseViewBox,
      minZoomWidth: config.minZoomWidth,
    });
    updateViewBoxDom(newVb);
  }, [baseViewBox, config, updateViewBoxDom]);

  const zoomOut = useCallback(() => {
    const vb = viewBoxRef.current;
    const cx = vb.x + vb.w / 2;
    const cy = vb.y + vb.h / 2;
    const newVb = calculateZoomedViewBox({
      zoomCenterX: cx,
      zoomCenterY: cy,
      factor: config.zoomOutFactor,
      currentViewBox: vb,
      baseViewBox,
      minZoomWidth: config.minZoomWidth,
    });
    updateViewBoxDom(newVb);
  }, [baseViewBox, config, updateViewBoxDom]);

  const resetZoom = useCallback(() => {
    updateViewBoxDom(baseViewBox);
  }, [baseViewBox, updateViewBoxDom]);

  useEffect(() => {
    updateViewBoxDom(baseViewBox);
  }, [baseViewBox, updateViewBoxDom]);

  const svgPointFromMouse = useCallback((e: MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return null;
    return clientToSvgPoint({
      clientX: e.clientX,
      clientY: e.clientY,
      rect: svg.getBoundingClientRect(),
      viewBox: viewBoxRef.current,
    });
  }, []);

  const activeHandlers = {
    cursor: panHandlers.isPanning
      ? MINIMAP_CURSORS.GRABBING
      : isSpacePressed
        ? MINIMAP_CURSORS.GRAB
        : MINIMAP_CURSORS.DEFAULT,
    zoomIn,
    zoomOut,
    resetZoom,
    ...panHandlers,
  } as const;

  const handlers = isDisabled ? MINIMAP_DISABLED_HANDLERS : activeHandlers;

  return {
    svgRef,
    isSpacePressed,
    svgPointFromMouse,
    ...handlers,
  };
};
